import React, { useState, useRef, useEffect } from 'react';
import { FiSearch, FiX, FiUser } from 'react-icons/fi';
import './MembroBuscaSelect.css';

/**
 * Campo de busca com autocomplete para selecionar um membro da irmandade.
 *
 * Props:
 *   membros       – array de membros { id, nome_completo, nomeCompleto, categoria, telefone, email }
 *   value         – id do membro selecionado (string ou number, vazio = nenhum)
 *   onChange      – (membro | null) => void
 *   loading       – boolean
 *   disabled      – boolean
 */
const MembroBuscaSelect = ({ membros = [], value, onChange, loading = false, disabled = false }) => {
  const nomeMembro = (m) => m?.nome_completo || m?.nomeCompleto || '';

  const membroAtual = membros.find(m => String(m.id) === String(value)) || null;

  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Fechar ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setAberto(false);
        setBusca('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const membrosFiltrados = busca.trim().length > 0
    ? membros.filter(m => nomeMembro(m).toLowerCase().includes(busca.toLowerCase()))
    : membros;

  const selecionar = (m) => {
    onChange(m);
    setAberto(false);
    setBusca('');
  };

  const limpar = (e) => {
    e.stopPropagation();
    onChange(null);
    setBusca('');
  };

  const abrirBusca = () => {
    if (disabled || loading) return;
    setAberto(true);
    setBusca('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  if (loading) {
    return <div className="mbs-loading">Carregando membros...</div>;
  }

  return (
    <div className="mbs-wrap" ref={wrapRef}>
      {/* Campo principal – mostra membro selecionado ou placeholder */}
      {!aberto ? (
        <div
          className={`mbs-field${disabled ? ' mbs-disabled' : ''}`}
          onClick={abrirBusca}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={e => e.key === 'Enter' && abrirBusca()}
          role="combobox"
          aria-expanded={false}
          aria-controls="mbs-dropdown"
          aria-haspopup="listbox"
        >
          {membroAtual ? (
            <>
              <FiUser className="mbs-icon" />
              <span className="mbs-nome">{nomeMembro(membroAtual)}</span>
              <span className="mbs-cat">
                {membroAtual.categoria === 'jovem' ? 'Jovem' : 'Adulto'}
              </span>
              {!disabled && (
                <button type="button" className="mbs-clear" onClick={limpar} title="Remover vínculo">
                  <FiX size={14} />
                </button>
              )}
            </>
          ) : (
            <>
              <FiSearch className="mbs-icon mbs-icon-muted" />
              <span className="mbs-placeholder">Buscar membro...</span>
            </>
          )}
        </div>
      ) : (
        /* Campo de busca ativo */
        <div className="mbs-search-wrap">
          <FiSearch className="mbs-search-icon" />
          <input
            ref={inputRef}
            type="search"
            className="mbs-search-input"
            placeholder="Digite o nome..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            onKeyDown={e => e.key === 'Escape' && setAberto(false)}
          />
          <button type="button" className="mbs-clear" onClick={() => setAberto(false)} title="Cancelar">
            <FiX size={14} />
          </button>
        </div>
      )}

      {/* Dropdown de resultados */}
      {aberto && (
        <ul className="mbs-dropdown">
          <li
            className="mbs-item mbs-item-none"
            onClick={() => selecionar(null)}
          >
            <em>Nenhum</em>
          </li>
          {membrosFiltrados.length === 0 ? (
            <li className="mbs-item mbs-item-empty">Nenhum membro encontrado.</li>
          ) : (
            membrosFiltrados.slice(0, 50).map(m => (
              <li
                key={m.id}
                className={`mbs-item${String(m.id) === String(value) ? ' mbs-item-ativo' : ''}`}
                onClick={() => selecionar(m)}
              >
                <span className="mbs-item-nome">{nomeMembro(m)}</span>
                <span className="mbs-item-cat">
                  {m.categoria === 'jovem' ? 'Jovem' : 'Adulto'}
                </span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default MembroBuscaSelect;
